// Tiny localStorage-backed ProgramsCache adapter for web-rnw. Web has no
// AsyncStorage, so without this the programs/progress lists start from null on
// every load and depend entirely on the network landing — a transient
// per-program progress failure then has nothing to fall back on and the Plan
// carousel order flips (Antenore 2026-06-18). Cache-first here mirrors the
// mobile AsyncStorage adapters ([[reference_web_rnw_needs_swr_cache]]).
import { LS_COPY_PREFIX, scopeGate, type CopyWrite } from "@heraion/shared";

import { webStorage } from "./webStorage";

// #397: everything lsCache keeps is a copy of the signed-in account's server
// rows, never unsent work (drafts live elsewhere), so lsCache is the one
// place that lists them for dropLsCopies. A prefix of its own, not an index:
// it survives reloads (a `workouts/result/<id>` kept last week is found by a
// tab that never opened it) and writes nothing beside the copy.
const PREFIX = LS_COPY_PREFIX;

// The same copies under their names from before the prefix, still on the
// browsers that ran an older build: swept with the rest. Frozen, it does not
// grow: a new copy goes through lsCache and gets the prefix.
const UNPREFIXED = new Set([
  "progress/sessionResults",
  "workouts/firstPage",
  "jl.programs.v1",
  "jl.programsProgress.v1",
  "jl.athleteTargets.v1",
  "logger/exercisesCache.v2",
  "jl.home.signals.v3",
]);
const UNPREFIXED_RESULT = "workouts/result/";

const isCopyKey = (key: string): boolean =>
  key.startsWith(PREFIX) || UNPREFIXED.has(key) || key.startsWith(UNPREFIXED_RESULT);

// What this tab wrote. The memory store (localStorage refused) cannot list
// its keys, and lives only as long as the tab: this set is all of it there.
const written = new Set<string>();

export function lsCache<T>(key: string) {
  const stored = `${PREFIX}${key}`;
  // #398: past an account change, no copy goes back (see scopeGate).
  const open = scopeGate(stored);
  /** The copy, read now: for a first render that cannot wait a tick. */
  const peek = (): T | null => {
    try {
      const raw = webStorage.getItem(stored);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null; // private mode / corrupt entry → treat as empty
    }
  };
  const write: CopyWrite<T> = async (value, scope) => {
    if (!open(scope)) return false;
    try {
      written.add(stored);
      webStorage.setItem(stored, JSON.stringify(value));
    } catch {
      // quota exceeded / private mode: non-fatal, network still drives.
    }
    return true;
  };
  return { peek, read: async (): Promise<T | null> => peek(), write };
}

/** Every copy lsCache kept, from localStorage and from the memory store it
 *  falls back to (auth/accountCaches.ts, on an account change). */
export function dropLsCopies(): void {
  for (const key of written) {
    try {
      webStorage.removeItem(key);
    } catch {
      // nothing to clean
    }
  }
  written.clear();
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && isCopyKey(key)) stale.push(key);
    }
    for (const key of stale) localStorage.removeItem(key);
  } catch {
    // localStorage refused: the copies were in memory, dropped above.
  }
}
